/**
 * DIE REGELN DES HTML-FILTERS — an EINER Stelle, weil zwei Werkzeuge sie
 * anwenden und nie auseinanderlaufen duerfen:
 *
 *   • im Browser DOMPurify (htmlFilter.ts) — mit dem echten Dokument;
 *   • auf dem Viewer-Server ein Durchlauf OHNE Browser-Dokument
 *     (htmlFilterServer.ts).
 *
 * Was hier steht, ist der Teil der Regeln, den WIR festlegen. Den Grundstock
 * (welche Tags und Attribute ueberhaupt als harmlos gelten) bringt DOMPurify
 * mit; die Server-Variante fuehrt ihn als eigene Liste und wird im Test gegen
 * DOMPurify verglichen (app/src/lib/__tests__/html-filter-server-gleich.test.ts).
 */

/**
 * Was eine eingebettete Seite darf. Bewusst NICHT dabei:
 * `allow-top-navigation` (den ganzen Tab umleiten), `allow-modals`
 * (alert/confirm ueber unserer Seite), `allow-downloads`.
 * `allow-same-origin` laesst der fremden Seite ihre EIGENE Herkunft (ohne das
 * funktionieren Player und eingebettete Werkzeuge nicht) — Zugriff auf unsere
 * Seite gibt es ihr nicht.
 */
export const IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-presentation';

/** Was die eingebettete Seite ueber uns erfaehrt: nur die Herkunft, nicht die Adresse des Scrollys. */
export const IFRAME_REFERRER = 'strict-origin-when-cross-origin';

/** Eine eingebettete Seite braucht eine echte Webadresse (http/https). */
export const NUR_WEBADRESSE = /^(https?:)?\/\//i;

/**
 * Domains, auf denen UNSERE Anwendungen laufen (mit Login im Browser).
 * Wegen `allow-same-origin` waere eine eingebettete Seite von dort mit
 * unserer Seite gleicher Herkunft: Skript darin (etwa eine hochgeladene
 * SVG-Datei unter /resource) duerfte dann das Login lesen. Fremde Seiten
 * betrifft das nicht. Inventur prod 03.10.2026: kein Scrolly bettet eine
 * eigene Domain ein (9 iframes: YouTube, passwords.yetzt.me, kicker).
 */
const EIGENE_DOMAINS = ['scrolly.com', 'scrolly.online'];

/** Zeigt eine Webadresse auf eine unserer eigenen Domains? */
export function istEigeneDomain(adresse: string): boolean {
  let host: string;
  try {
    host = new URL(adresse, 'https://fremd.invalid').hostname.toLowerCase();
  } catch {
    // Nicht lesbar: lieber als eigene behandeln (das iframe faellt weg).
    return true;
  }
  host = host.replace(/\.+$/, '');
  return EIGENE_DOMAINS.some(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );
}

/** Geraete-Rechte, die ein `allow`-Attribut an die fremde Seite weiterreichen darf. */
const IFRAME_ALLOW_ERLAUBT = new Set([
  'accelerometer',
  'autoplay',
  'clipboard-write',
  'encrypted-media',
  'fullscreen',
  'gyroscope',
  'picture-in-picture',
  'web-share',
]);

/**
 * `allow` einer eingebetteten Seite auf das Unbedenkliche kuerzen. `null`,
 * wenn nichts uebrig bleibt — Kamera, Mikrofon und Standort lassen sich nicht
 * durchreichen.
 */
export function gefiltertesAllow(allow: string): string | null {
  const bleibt = allow
    .split(';')
    .map((teil) => teil.trim())
    .filter((teil) =>
      IFRAME_ALLOW_ERLAUBT.has(teil.split(/\s+/)[0].toLowerCase()),
    );
  return bleibt.length > 0 ? bleibt.join('; ') : null;
}

/**
 * Ein Link in einen neuen Tab bekommt nie Zugriff auf unser Fenster: `rel`
 * enthaelt danach `noopener` und `noreferrer`.
 *
 * Was der Autor (oder der Texteditor) sonst hineingeschrieben hat, BLEIBT —
 * der Texteditor kennzeichnet Links mit `nofollow`, damit Suchmaschinen ihnen
 * nicht folgen. Die erste Fassung des Filters hat `rel` ueberschrieben und
 * die Kennzeichnung damit entfernt (auf dev gemessen, 02.10.2026).
 */
const LINK_REL_PFLICHT = ['noopener', 'noreferrer'];

export function sicheresRel(vorhanden: string | null | undefined): string {
  const wert = (vorhanden ?? '').trim();
  const teile = wert.split(/\s+/).filter(Boolean);
  const klein = teile.map((teil) => teil.toLowerCase());
  const fehlt = LINK_REL_PFLICHT.filter((teil) => !klein.includes(teil));
  // Nichts zu ergaenzen: der Wert bleibt Zeichen fuer Zeichen, wie er war.
  if (fehlt.length === 0) return wert;
  return [...teile, ...fehlt].join(' ');
}

/** Tags ueber den Grundstock hinaus. */
export const ZUSATZ_TAGS = ['iframe'];

/** Attribute ueber den Grundstock hinaus. */
export const ZUSATZ_ATTRIBUTE = [
  'target',
  'allow',
  'allowfullscreen',
  'frameborder',
  'scrolling',
  'sandbox',
  'referrerpolicy',
  'loading',
  'playsinline',
  'webkit-playsinline',
];

/** Anmelde-Attrappen und alles, was fremden Inhalt ungeprueft nachlaedt. */
export const VERBOTENE_TAGS = [
  'form',
  'input',
  'button',
  'textarea',
  'select',
  'option',
  'optgroup',
  'datalist',
  'fieldset',
  'output',
  'dialog',
  'object',
  'embed',
  'base',
  'meta',
  'link',
  'frame',
  'frameset',
  'noscript',
  'template',
];

/**
 * `name` ist verboten, weil ein benanntes Bild oder Fenster Eigenschaften
 * unseres Dokuments ueberdecken kann (`<img name="cookie">`). Kein
 * Bestands-Text benutzt es (gemessen 02.10.2026, 1242 Texte). `id` bleibt.
 */
export const VERBOTENE_ATTRIBUTE = [
  'srcdoc',
  'formaction',
  'form',
  'ping',
  'autofocus',
  'name',
];

/** Ein `<`, hinter dem ein Tag, ein Schluss-Tag oder ein Kommentar beginnen koennte. */
const TAG_ANFANG = /<(?=[/\w!])/g;
const TAG_ANFANG_PROBE = /<[/\w!]/;

/** Steht im Text etwas, das wie der Anfang eines Tags aussieht? */
export function enthaeltTagAnfang(text: string): boolean {
  return TAG_ANFANG_PROBE.test(text);
}

/**
 * STIL-BLOECKE VON AUTOREN ERHALTEN (gemessen am Bestand, 02.10.2026): Zehn
 * oeffentliche Scrollys faerben ihre Links ueber ein `<style>` im Slide-Text.
 * Der Markdown-Wandler zerlegt so einen Block an jeder Leerzeile in Absaetze
 * — im Stil-Text stehen dann `</p> <p>`. DOMPurify verwirft einen Stil-Block,
 * in dem etwas wie ein Tag vorkommt (Schutz vor Umdeutung beim erneuten
 * Einlesen), die Linkfarben waeren also weg gewesen.
 *
 * Deshalb verliert der INHALT eines Stil-Blocks vorab jedes `<`, hinter dem
 * ein Tag beginnen koennte. Fuer den Browser aendert das nichts: Die Reste
 * (`/p> p>`) sind genauso ungueltiges CSS wie vorher (`</p> <p>`), dieselben
 * Regeln greifen oder greifen nicht. Aus dem Block ausbrechen kann danach
 * nichts mehr.
 *
 * Bewusst als Schleife statt als EIN Muster ueber den ganzen Block: Ein
 * Muster wie `<style…>…</style>` sucht bei jedem offenen `<style` bis zum
 * Textende — Tausende offene `<style` haetten den Server lange beschaeftigt.
 * So geht es einmal von vorn nach hinten durch.
 */
export function entschaerfeStilBloecke(html: string): string {
  const auf = /<style\b/gi;
  const zu = /<\/style\s*>/gi;
  let aus = '';
  let stand = 0;
  for (;;) {
    auf.lastIndex = stand;
    const anfang = auf.exec(html);
    if (!anfang) break;
    const inhaltAb = html.indexOf('>', anfang.index) + 1;
    if (inhaltAb === 0) break;
    zu.lastIndex = inhaltAb;
    const schluss = zu.exec(html);
    if (!schluss) break;
    aus +=
      html.slice(stand, inhaltAb) +
      html.slice(inhaltAb, schluss.index).replace(TAG_ANFANG, '') +
      schluss[0];
    stand = schluss.index + schluss[0].length;
  }
  return stand === 0 ? html : aus + html.slice(stand);
}

const MASKE: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Ohne jedes Werkzeug zum Filtern: alles HTML entfernen und den Rest
 * maskieren. Der Notbehelf, wenn der Filter fehlt, scheitert oder den Text
 * ablehnt — einmal von vorn nach hinten, egal wie der Text aussieht.
 */
export function nurText(html: string): string {
  let text = '';
  let stand = 0;
  while (stand < html.length) {
    const auf = html.indexOf('<', stand);
    if (auf === -1) {
      text += html.slice(stand);
      break;
    }
    text += html.slice(stand, auf);
    // Skript- und Stil-Text ist nichts zum Lesen: samt Inhalt weg.
    const roh = /^<(script|style)\b/i.exec(html.slice(auf, auf + 8));
    let ende: number;
    if (roh) {
      const schluss = new RegExp(`</${roh[1]}\\s*>`, 'gi');
      schluss.lastIndex = auf;
      const treffer = schluss.exec(html);
      if (!treffer) break;
      ende = treffer.index + treffer[0].length;
    } else {
      ende = html.indexOf('>', auf) + 1;
      if (ende === 0) {
        // Ein einzelnes `<` ohne Ende ist Text („a < b").
        text += html.slice(auf);
        break;
      }
    }
    // Wo ein Tag stand, bleibt ein Leerzeichen — sonst kleben Woerter zusammen.
    text += ' ';
    stand = ende;
  }
  return text.replace(/[&<>"']/g, (zeichen) => MASKE[zeichen]);
}
